/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-28 14:51:35                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-04 15:40:17                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * TabsNav: tabs navigation, display tab layer
 * useEffect: get current tab
 * useEffect: force tab navigation on currState change
 * handleTabChange: Handle tab change
 */

/* Imports */
import React, {
  ComponentType,
  createElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Tabs,
  TabsBody,
  TabsHeader,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import { useSearchParams } from "react-router-dom";
/***/

/* Styles */
import "./TabsNav.scss";
/***/

/**
 * TabsNav: tabs navigation, display tab layer
 * @param defaultTab: default tab to print
 * @param data: tab data
 * @param tabs: tabs list
 * @param onTabChange: callback when tab changes
 */
export function TabsNav<T>({
  defaultTab,
  data,
  tabs,
  onTabChange,
}: {
  defaultTab: string;
  data: T;
  tabs: {
    label: string | ReactNode;
    value: string;
    icon: any;
    Component: ComponentType<{ data: T }> | ReactNode;
    disabled?: boolean;
  }[];
  onTabChange?: (value: string) => void;
}) {
  const [params, setParams] = useSearchParams();
  const [currTab, setCurrTab] = useState<string>(defaultTab);
  const tabRef = useRef<any[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  /**
   * useEffect: get current tab
   * @param params - query params
   * @param defaultTab - default tab displayed
   */
  useEffect(() => {
    const tab = params.get("tab");
    if (tab) setCurrTab(params.get("tab") as string);
  }, [params]);
  /***/

  /**
   * useEffect: force tab navigation on currState change
   * @param currTab - current tab value
   * @param tabs - tabs list
   */
  useEffect(() => {
    const index = tabs.findIndex((el) => el.value === currTab);
    if (index !== -1) tabRef.current[index].click();
  }, [currTab, tabs]);
  /***/

  /**
   * handleTabChange: Handle tab change
   * @param value - new tab
   */
  const handleTabChange = (value: string) => {
    const tab = tabs.find((tab) => tab.value === value);
    if (tab?.disabled) {
      return;
    }

    if (currTab !== value) {
      setCurrTab(value);
      // Merge rather than replace: setParams({tab}) would drop every sibling search param, so any
      // view keeping filter state in the URL (period, date range, entity filters) would silently
      // reset each time the user switched tab.
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      });

      if (onTabChange) {
        onTabChange(value);
      }
    }
  };
  /***/

  /**
   * handleScroll: Gérer l'affichage des ombres selon la position de défilement
   */
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // Ombre gauche visible si on a défilé vers la droite
    setShowLeftShadow(scrollLeft > 5);
    
    // Ombre droite visible s'il reste du contenu à droite
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 5);
  };

  /**
   * useEffect: Vérif si besoin d'ombre
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    handleScroll();

    container.addEventListener("scroll", handleScroll);
    

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [tabs]);

  return (
    <div className="tabs-nav-mobile">
      <Tabs value={defaultTab} className="overflow-visible">
        <div className="tabs-header-container" ref={scrollContainerRef}>
          <TabsHeader
            className="z-0 rounded-none bg-transparent p-0 mb-4 border-b border-gray-300 tabs-header"
            indicatorProps={{
              className:
                "bg-transparent border-b-4 border-sky-500 shadow-none rounded-none",
            }}
          >
            {tabs.map(({ label, value, icon, disabled }, i) => (
              <Tab
                key={value}
                value={value}
                className={`w-fit px-4 pb-4 tab-item ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => !disabled && handleTabChange(value)}
                ref={(ref) => (tabRef.current[i] = ref)}
                disabled={disabled}
              >
                <div
                  className={`flex items-center gap-2 ${
                    currTab === value ? "text-sky-500" : ""
                  }`}
                >
                  {icon && createElement(icon, { className: "w-5 h-5" })}
                  {label}
                </div>
              </Tab>
            ))}
          </TabsHeader>
        </div>

        {/* Ombres de défilement */}
        <div className={`scroll-shadow-left ${showLeftShadow ? "visible" : ""}`} />
        <div className={`scroll-shadow-right ${showRightShadow ? "visible" : ""}`} />

        <TabsBody className="overflow-visible">
          {tabs.map(({ value, Component }) => (
            <TabPanel key={value} value={value} className="p-0">
              {React.isValidElement(Component)
                ? Component
                : createElement(Component as ComponentType<{ data: T }>, {
                  data,
                })}
            </TabPanel>
          ))}
        </TabsBody>
      </Tabs>
    </div>
  );
}
